import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Loader2, Play, Copy, Target, MessagesSquare, ArrowRight, ShieldCheck } from "lucide-react";
import { ASSETS } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FieldUpdatePage() {
  const { id } = useParams();
  const [fu, setFu] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    axios.get(`${API}/field-updates/${id}`).then((r) => setFu(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-linen flex flex-col items-center justify-center px-6 text-center" data-testid="field-update-not-found">
        <p className="font-heading text-2xl text-navy">This Field Update isn't available.</p>
        <p className="mt-2 text-sm text-ink-500">The link may be old. Check your inbox for the latest one.</p>
        <Link to="/" className="mt-6 text-sm text-teal hover:underline">Back to Great Rate Check</Link>
      </div>
    );
  }
  if (!fu) {
    return (
      <div className="min-h-screen bg-linen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-teal" />
      </div>
    );
  }

  const copyScript = () =>
    navigator.clipboard
      .writeText(fu.script || "")
      .then(() => toast.success("Script copied — swap in a name and send it"))
      .catch(() => toast.error("Couldn't copy — select the text manually."));

  const when = fu.created_at
    ? new Date(fu.created_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-linen text-navy antialiased" data-testid="field-update-page">
      <header className="bg-white border-b border-linen-border">
        <div className="container-grc h-16 flex items-center justify-between">
          <Link to="/"><img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-8 w-auto" /></Link>
          <span className="text-[11px] uppercase tracking-widest text-ink-400">The Field Update</span>
        </div>
      </header>

      <section style={{ background: "linear-gradient(115deg,#08192F 0%,#0F2A47 50%,#17385e 85%,#1F8F8B 165%)" }}>
        <div className="container-grc py-12 sm:py-16 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#A6D24A]">Field Update{when ? ` · ${when}` : ""}</p>
          <h1 className="mt-3 font-heading font-medium text-3xl sm:text-4xl text-white leading-[1.12]">{fu.subject}</h1>
        </div>
      </section>

      <main className="container-grc py-12 max-w-3xl space-y-8">
        {fu.video_url && (
          <div data-testid="field-update-video">
            {fu.youtube_id ? (
              <div className="rounded-3xl overflow-hidden border border-linen-border shadow-soft aspect-video bg-navy">
                <iframe
                  src={`https://www.youtube.com/embed/${fu.youtube_id}`}
                  title={fu.subject}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            ) : (
              <a
                href={fu.video_url}
                target="_blank"
                rel="noreferrer"
                data-testid="field-update-video-link"
                className="group block rounded-3xl overflow-hidden border border-linen-border shadow-soft bg-navy"
              >
                <div className="relative aspect-video">
                  <img
                    src={fu.thumbnail_url || "/fu-video-poster.jpg"}
                    alt={fu.subject}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-navy/20 group-hover:bg-navy/10 transition-colors">
                    <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime text-navy-deep shadow-cta group-hover:scale-105 transition-transform">
                      <Play size={24} fill="currentColor" />
                    </span>
                  </span>
                </div>
              </a>
            )}
          </div>
        )}

        {fu.brief && (
          <section data-testid="field-update-brief">
            <h2 className="text-[11px] uppercase tracking-widest text-teal font-bold">The written brief</h2>
            <div
              className="grc-rte-view mt-3 rounded-3xl bg-white border border-linen-border shadow-soft p-6 sm:p-8 text-[15px] text-navy leading-relaxed"
              dangerouslySetInnerHTML={{ __html: fu.brief }}
            />
            <p className="mt-2 text-xs text-ink-400">Short on time? This is the whole update in writing.</p>
          </section>
        )}

        <section data-testid="field-update-message">
          <h2 className="text-[11px] uppercase tracking-widest text-teal font-bold">This week's message</h2>
          <p className="mt-3 text-[15px] text-navy leading-relaxed whitespace-pre-wrap">{fu.message}</p>
        </section>

        {fu.script && (
          <section data-testid="field-update-script">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-[11px] uppercase tracking-widest text-teal font-bold">Copy / paste outreach</h2>
              <button
                onClick={copyScript}
                data-testid="field-update-copy-script"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold bg-navy text-white px-3 py-1.5 rounded-full hover:bg-[#173a5f] transition-colors"
              >
                <Copy size={11} /> Copy
              </button>
            </div>
            <div className="mt-3 rounded-2xl bg-white border border-dashed border-[#C9C2A9] p-5 text-[15px] text-navy leading-relaxed whitespace-pre-wrap">
              {fu.script}
            </div>
          </section>
        )}

        {fu.confidence_note && (
          <section className="rounded-2xl border-l-4 border-teal bg-teal-light/30 px-5 py-4" data-testid="field-update-confidence">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-teal font-bold">
              <ShieldCheck size={12} /> Confidence check
            </div>
            <p className="mt-1.5 text-sm text-navy leading-relaxed">{fu.confidence_note}</p>
          </section>
        )}

        {fu.action_item && (
          <section className="rounded-2xl bg-lime-soft border border-[#A6D24A] px-5 py-5" data-testid="field-update-action">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-navy-deep font-bold">
              <Target size={12} /> This week's action item
            </div>
            <p className="mt-1.5 text-base font-semibold text-navy leading-snug">{fu.action_item}</p>
          </section>
        )}

        {fu.community?.enabled && (
          <section className="rounded-3xl p-7 text-white" style={{ background: "linear-gradient(140deg,#0F2A47 0%,#1F8F8B 180%)" }} data-testid="field-update-community">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[#A6D24A]">
              <MessagesSquare size={13} /> Keep the conversation going
            </div>
            <h3 className="mt-1 font-heading font-medium text-2xl">{fu.community.label}</h3>
            {fu.community.note && <p className="mt-2 text-sm text-white/75 max-w-lg">{fu.community.note}</p>}
            <a
              href={fu.community.url}
              target="_blank"
              rel="noreferrer"
              data-testid="field-update-community-join"
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#A6D24A] text-[#08192F] font-semibold text-sm px-5 py-2.5 hover:brightness-105 transition-all"
            >
              Join the group <ArrowRight size={14} />
            </a>
          </section>
        )}

        <p className="pt-2 text-center text-xs text-ink-400">
          Safe to forward to your assistant · Great Rate Check · NEXA Mortgage LLC
        </p>
      </main>
      <Toaster position="top-center" richColors />
    </div>
  );
}
